import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, Layers, X, Check, Loader2,
  AlertCircle, Play, Pause, Trash2, Search
} from 'lucide-react';
import { api } from '../../services/api';

const API_URL = api.getApiUrl();

const OVERLAY_CATEGORIES = [
  { id: 'glitch',       name: 'Glitch',        icon: '⚡' },
  { id: 'light',        name: 'Light Leaks',   icon: '✨' },
  { id: 'particles',    name: 'Partículas',    icon: '🌟' },
  { id: 'smoke',        name: 'Smoke & Fog',   icon: '🌫️' },
  { id: 'transitions',  name: 'Transições',    icon: '🔀' },
  { id: 'lens',         name: 'Lens Flare',    icon: '🔆' },
  { id: 'grain',        name: 'Film Grain',    icon: '🎞️' },
  { id: 'outros',       name: 'Outros',        icon: '📁' },
];

const AdminAddOverlays = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('upload');
  const [category, setCategory] = useState('glitch');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [playingId, setPlayingId] = useState(null);

  const [overlayList, setOverlayList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadOverlayList = async () => {
    setLoadingList(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/overlays`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao carregar Overlays');
      const data = await res.json();
      setOverlayList(data || []);
    } catch (err) {
      console.error('Erro ao carregar lista:', err);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'manage') loadOverlayList();
  }, [activeTab]);

  const handleDeleteOverlay = async (overlayId) => {
    setDeletingId(overlayId);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/overlays/${overlayId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erro ao deletar');
      setOverlayList(prev => prev.filter(o => o.id !== overlayId));
      setConfirmDelete(null);
    } catch (err) {
      alert('Erro ao deletar Overlay.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const videoFiles = files.filter(f =>
      f.type.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv)$/i.test(f.name)
    );
    if (videoFiles.length === 0) {
      setError('Nenhum arquivo de vídeo válido selecionado');
      return;
    }
    const newFiles = videoFiles.map(f => ({
      id: Math.random().toString(36).substr(2, 9),
      file: f,
      title: f.name.replace(/\.(mp4|mov|webm|avi|mkv)$/i, ''),
      tags: [],
      status: 'pending',
      previewUrl: URL.createObjectURL(f)
    }));
    setSelectedFiles(prev => [...prev, ...newFiles]);
    setError('');
  };

  const removeFile = (id) => {
    setSelectedFiles(prev => {
      const f = prev.find(x => x.id === id);
      if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      return prev.filter(x => x.id !== id);
    });
  };

  const updateFileTitle = (id, title) =>
    setSelectedFiles(prev => prev.map(f => f.id === id ? { ...f, title } : f));

  const togglePlay = (id) => {
    const vid = document.getElementById(`vid-${id}`);
    if (!vid) return;
    if (playingId === id) {
      vid.pause();
      setPlayingId(null);
    } else {
      if (playingId) {
        const prev = document.getElementById(`vid-${playingId}`);
        if (prev) prev.pause();
      }
      vid.play();
      setPlayingId(id);
    }
  };

  const handleUploadAll = async () => {
    if (selectedFiles.length === 0) {
      setError('Selecione pelo menos um arquivo');
      return;
    }
    setSaving(true);
    setError('');
    for (const fileItem of selectedFiles) {
      if (fileItem.status === 'success') continue;
      setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'uploading' } : f));
      try {
        const formData = new FormData();
        formData.append('file', fileItem.file);
        const token = localStorage.getItem('admin_token');
        if (!token) throw new Error('Token não encontrado. Faça login novamente.');

        const uploadRes = await fetch(`${API_URL}/api/upload-overlay-r2`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (uploadRes.status === 401) throw new Error('Sessão expirada. Faça login novamente.');
        if (!uploadRes.ok) {
          const err = await uploadRes.json().catch(() => ({}));
          throw new Error(err.detail || `Erro no upload: ${uploadRes.status}`);
        }
        const uploadData = await uploadRes.json();

        const payload = {
          title: fileItem.title,
          category,
          tags: fileItem.tags,
          video_url: uploadData.video_url,
          storage_provider: 'r2',
          storage_key: uploadData.storage_key,
          format: fileItem.file.name.split('.').pop().toLowerCase() || 'mp4',
          is_premium: false
        };

        const createRes = await fetch(`${API_URL}/api/overlays`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!createRes.ok) {
          const err = await createRes.json().catch(() => ({}));
          throw new Error(err.detail || 'Erro ao criar overlay');
        }

        setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'success' } : f));
      } catch (err) {
        if (err.message.includes('Sessão expirada') || err.message.includes('Token')) {
          setError(err.message);
          setSaving(false);
          setTimeout(() => { localStorage.removeItem('admin_token'); navigate('/admin/login'); }, 3000);
          return;
        }
        setSelectedFiles(prev => prev.map(f => f.id === fileItem.id ? { ...f, status: 'error', errorMsg: err.message } : f));
      }
    }
    setSaving(false);
    const allSuccess = selectedFiles.every(f => f.status === 'success');
    if (allSuccess) setTimeout(() => setSelectedFiles([]), 2000);
  };

  const filteredOverlays = overlayList.filter(o => {
    const matchCat = filterCategory === 'all' || o.category === filterCategory;
    const matchSearch = !searchQuery || o.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <div className="max-w-5xl mx-auto p-8">
        <div className="flex items-center justify-between mb-8">
          <Link to="/admin" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Voltar ao Admin
          </Link>
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${activeTab === 'upload' ? 'bg-purple-500 text-white' : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'}`}
          >
            <Upload className="w-4 h-4" />
            Upload Overlay
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${activeTab === 'manage' ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'}`}
          >
            <Trash2 className="w-4 h-4" />
            Gerenciar Overlays
            {overlayList.length > 0 && (
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{overlayList.length}</span>
            )}
          </button>
        </div>

        {/* TAB UPLOAD */}
        {activeTab === 'upload' && (
          <div className="bg-[#0D0D0D] rounded-2xl border border-white/10 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center">
                <Layers className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Adicionar Overlay</h1>
                <p className="text-slate-400 text-sm">Transições e efeitos visuais (R2)</p>
              </div>
            </div>

            {error && (
              <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-red-500 font-bold text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Categoria</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {OVERLAY_CATEGORIES.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategory(cat.id)}
                    className={`p-3 rounded-xl border font-bold text-xs transition-all ${category === cat.id ? 'bg-purple-500 border-purple-500 text-white' : 'bg-white/5 border-white/10 text-gray-400 hover:text-white hover:border-white/20'}`}
                  >
                    <span className="text-lg block mb-1">{cat.icon}</span>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Arquivos de Vídeo</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500/50 transition-all bg-white/5 hover:bg-white/10"
              >
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-300 font-bold">Clique para selecionar vídeos</p>
                <p className="text-gray-500 text-sm mt-1">MP4, MOV, WEBM — múltiplos arquivos</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*,.mp4,.mov,.webm,.avi"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {selectedFiles.length > 0 && (
              <div className="mb-6 space-y-3">
                {selectedFiles.map(fileItem => (
                  <div key={fileItem.id} className={`bg-white/5 rounded-xl p-4 border ${fileItem.status === 'success' ? 'border-green-500/50' : fileItem.status === 'error' ? 'border-red-500/50' : 'border-white/10'}`}>
                    <div className="flex items-center gap-3">
                      <video id={`vid-${fileItem.id}`} src={fileItem.previewUrl} className="w-16 h-10 rounded object-cover bg-black" />
                      <button
                        onClick={() => togglePlay(fileItem.id)}
                        className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 flex-shrink-0"
                      >
                        {playingId === fileItem.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <input
                        value={fileItem.title}
                        onChange={e => updateFileTitle(fileItem.id, e.target.value)}
                        className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <div className="flex items-center gap-2">
                        {fileItem.status === 'uploading' && <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />}
                        {fileItem.status === 'success' && <Check className="w-5 h-5 text-green-400" />}
                        {fileItem.status === 'error' && <AlertCircle className="w-5 h-5 text-red-400" title={fileItem.errorMsg} />}
                        <button onClick={() => removeFile(fileItem.id)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedFiles.length > 0 && (
              <button
                onClick={handleUploadAll}
                disabled={saving}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl font-bold text-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {saving ? <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</> : <><Upload className="w-5 h-5" /> Enviar {selectedFiles.length} Overlay(s)</>}
              </button>
            )}
          </div>
        )}

        {/* TAB MANAGE */}
        {activeTab === 'manage' && (
          <div className="bg-[#0D0D0D] rounded-2xl border border-white/10 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Gerenciar Overlays</h2>
              <button onClick={loadOverlayList} className="p-2 rounded-lg bg-white/5 hover:bg-white/10">
                <Search className="w-4 h-4" />
              </button>
            </div>

            <div className="flex gap-3 mb-6 flex-wrap">
              <button
                onClick={() => setFilterCategory('all')}
                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${filterCategory === 'all' ? 'bg-purple-500 text-white' : 'bg-white/5 text-gray-400 hover:text-white'}`}
              >
                Todas
              </button>
              {OVERLAY_CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setFilterCategory(cat.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${filterCategory === cat.id ? 'bg-purple-500 text-white' : 'bg-white/5 text-gray-400 hover:text-white'}`}
                >
                  {cat.icon} {cat.name}
                </button>
              ))}
            </div>

            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Buscar overlay..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {loadingList ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
              </div>
            ) : (
              <div className="space-y-2">
                {filteredOverlays.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Layers className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nenhum overlay encontrado</p>
                  </div>
                ) : filteredOverlays.map(overlay => (
                  <div key={overlay.id} className="bg-white/5 rounded-xl p-4 flex items-center gap-4 border border-white/5 hover:border-white/10 transition-all">
                    <div className="w-16 h-10 rounded bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Layers className="w-6 h-6 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{overlay.title}</p>
                      <p className="text-xs text-gray-400 capitalize">{overlay.category} • {overlay.download_count || 0} downloads</p>
                    </div>
                    {confirmDelete === overlay.id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-400">Confirmar?</span>
                        <button
                          onClick={() => handleDeleteOverlay(overlay.id)}
                          disabled={deletingId === overlay.id}
                          className="px-3 py-1.5 bg-red-500 rounded-lg text-xs font-bold hover:bg-red-600"
                        >
                          {deletingId === overlay.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Deletar'}
                        </button>
                        <button onClick={() => setConfirmDelete(null)} className="px-3 py-1.5 bg-white/10 rounded-lg text-xs font-bold">
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(overlay.id)}
                        className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAddOverlays;
